import {
  CreditCardOutlined,
  DiffOutlined,
  DollarOutlined,
  FileTextOutlined,
  LockOutlined,
  RollbackOutlined,
  WalletOutlined
} from "@ant-design/icons"
import type * as React from "react"

const FINANCE_ITEM_STATUSES = {
  PLANNED: 0,
  PENDING: 1,
  COMPLETED: 2,
  CANCELLED: 3,
  PARTIAL: 4,
  NEW: 5
} as const

type FinanceItemStatusesKeys = keyof typeof FINANCE_ITEM_STATUSES
type FinanceItemStatuses =
  (typeof FINANCE_ITEM_STATUSES)[FinanceItemStatusesKeys]

// Completed or Cancelled — no more payments or attachments may be added.
const isFinanceItemLocked = (status: number | null | undefined): boolean =>
  status === FINANCE_ITEM_STATUSES.COMPLETED ||
  status === FINANCE_ITEM_STATUSES.CANCELLED

const FINANCE_ITEM_STATUSES_MAP: Record<
  FinanceItemStatuses,
  {
    id: FinanceItemStatuses
    value: FinanceItemStatuses
    label: string
    style: React.CSSProperties
  }
> = {
  [FINANCE_ITEM_STATUSES.PLANNED]: {
    id: FINANCE_ITEM_STATUSES.PLANNED,
    value: FINANCE_ITEM_STATUSES.PLANNED,
    label: "Planned",
    style: {
      color: "#1976D2",
      backgroundColor: "#E3F2FD",
      borderColor: "#E3F2FD"
    }
  },
  [FINANCE_ITEM_STATUSES.PENDING]: {
    id: FINANCE_ITEM_STATUSES.PENDING,
    value: FINANCE_ITEM_STATUSES.PENDING,
    label: "Pending",
    style: {
      color: "#FF9800",
      backgroundColor: "#FFF3E0",
      borderColor: "#FFF3E0"
    }
  },
  [FINANCE_ITEM_STATUSES.COMPLETED]: {
    id: FINANCE_ITEM_STATUSES.COMPLETED,
    value: FINANCE_ITEM_STATUSES.COMPLETED,
    label: "Completed",
    style: {
      color: "#4CAF50",
      backgroundColor: "#E8F5E9",
      borderColor: "#E8F5E9"
    }
  },
  [FINANCE_ITEM_STATUSES.CANCELLED]: {
    id: FINANCE_ITEM_STATUSES.CANCELLED,
    value: FINANCE_ITEM_STATUSES.CANCELLED,
    label: "Cancelled",
    style: {
      color: "#F44336",
      backgroundColor: "#FFEBEE",
      borderColor: "#FFEBEE"
    }
  },
  [FINANCE_ITEM_STATUSES.PARTIAL]: {
    id: FINANCE_ITEM_STATUSES.PARTIAL,
    value: FINANCE_ITEM_STATUSES.PARTIAL,
    label: "Partial",
    style: {
      color: "#0097A7",
      backgroundColor: "#E0F7FA",
      borderColor: "#E0F7FA"
    }
  },
  [FINANCE_ITEM_STATUSES.NEW]: {
    id: FINANCE_ITEM_STATUSES.NEW,
    value: FINANCE_ITEM_STATUSES.NEW,
    label: "New",
    style: {
      color: "#7B1FA2",
      backgroundColor: "#F3E5F5",
      borderColor: "#F3E5F5"
    }
  }
}

const FINANCE_ITEM_TYPES = {
  RECEIVABLE: 1,
  PAYABLE: 2
} as const

type FinanceItemTypesKeys = keyof typeof FINANCE_ITEM_TYPES
type FinanceItemTypes = (typeof FINANCE_ITEM_TYPES)[FinanceItemTypesKeys]

const FINANCE_ITEM_TYPES_MAP: Record<
  FinanceItemTypes,
  {
    id: FinanceItemTypes
    value: FinanceItemTypes
    label: string
    style: React.CSSProperties
  }
> = {
  [FINANCE_ITEM_TYPES.RECEIVABLE]: {
    id: FINANCE_ITEM_TYPES.RECEIVABLE,
    value: FINANCE_ITEM_TYPES.RECEIVABLE,
    label: "Receivable",
    style: {
      color: "#2E7D32",
      backgroundColor: "#E8F5E9",
      borderColor: "#E8F5E9"
    }
  },
  [FINANCE_ITEM_TYPES.PAYABLE]: {
    id: FINANCE_ITEM_TYPES.PAYABLE,
    value: FINANCE_ITEM_TYPES.PAYABLE,
    label: "Payable",
    style: {
      color: "#EF6C00",
      backgroundColor: "#FFF3E0",
      borderColor: "#FFF3E0"
    }
  }
}

const TRANSACTION_KINDS = {
  STANDARD: "standard",
  REFUND: "refund",
  CREDIT_MEMO: "credit_memo",
  RETENTION_RELEASE: "retention_release",
  CR_CREDIT: "cr_credit",
  VOID: "void"
} as const

type TransactionKindKeys = keyof typeof TRANSACTION_KINDS
type TransactionKind = (typeof TRANSACTION_KINDS)[TransactionKindKeys]

const TRANSACTION_KIND_LABELS: Record<TransactionKind, string> = {
  [TRANSACTION_KINDS.STANDARD]: "Standard",
  [TRANSACTION_KINDS.REFUND]: "Refund",
  [TRANSACTION_KINDS.CREDIT_MEMO]: "Credit Memo",
  [TRANSACTION_KINDS.RETENTION_RELEASE]: "Retention Release",
  [TRANSACTION_KINDS.CR_CREDIT]: "CR Credit",
  [TRANSACTION_KINDS.VOID]: "Void"
}

const TRANSACTION_KIND_OPTIONS = Object.values(TRANSACTION_KINDS).map(
  (value) => ({
    label: TRANSACTION_KIND_LABELS[value],
    value
  })
)

const ENTRY_TYPES = {
  INVOICE: "invoice",
  PAYMENT: "payment",
  DEPOSIT: "deposit",
  EXPENSE: "expense",
  REFUND: "refund",
  RETENTION: "retention",
  CHANGE_ORDER: "change_order"
} as const

type EntryTypeKeys = keyof typeof ENTRY_TYPES
type EntryType = (typeof ENTRY_TYPES)[EntryTypeKeys]

const ENTRY_TYPE_LABELS: Record<EntryType, string> = {
  [ENTRY_TYPES.INVOICE]: "Invoice",
  [ENTRY_TYPES.PAYMENT]: "Payment",
  [ENTRY_TYPES.DEPOSIT]: "Deposit",
  [ENTRY_TYPES.EXPENSE]: "Expense",
  [ENTRY_TYPES.REFUND]: "Refund",
  [ENTRY_TYPES.RETENTION]: "Retention",
  [ENTRY_TYPES.CHANGE_ORDER]: "Change Order"
}

const ENTRY_TYPE_ICONS: Record<EntryType, React.ComponentType> = {
  [ENTRY_TYPES.INVOICE]: FileTextOutlined,
  [ENTRY_TYPES.PAYMENT]: CreditCardOutlined,
  [ENTRY_TYPES.DEPOSIT]: WalletOutlined,
  [ENTRY_TYPES.EXPENSE]: DollarOutlined,
  [ENTRY_TYPES.REFUND]: RollbackOutlined,
  [ENTRY_TYPES.RETENTION]: LockOutlined,
  [ENTRY_TYPES.CHANGE_ORDER]: DiffOutlined
}

// CU-869dx98pu: the picker is reduced to Payment / Deposit / Expense. Legacy
// values keep their label + icon above so existing rows still render.
const ENTRY_TYPE_PICKER_VALUES: EntryType[] = [
  ENTRY_TYPES.PAYMENT,
  ENTRY_TYPES.DEPOSIT,
  ENTRY_TYPES.EXPENSE
]

const ENTRY_TYPE_OPTIONS = ENTRY_TYPE_PICKER_VALUES.map((value) => ({
  label: ENTRY_TYPE_LABELS[value],
  value
}))

export {
  ENTRY_TYPE_ICONS,
  ENTRY_TYPE_LABELS,
  ENTRY_TYPE_OPTIONS,
  ENTRY_TYPES,
  FINANCE_ITEM_STATUSES,
  FINANCE_ITEM_STATUSES_MAP,
  FINANCE_ITEM_TYPES,
  FINANCE_ITEM_TYPES_MAP,
  isFinanceItemLocked,
  TRANSACTION_KIND_LABELS,
  TRANSACTION_KIND_OPTIONS,
  TRANSACTION_KINDS,
  type EntryType,
  type FinanceItemStatuses,
  type FinanceItemTypes,
  type TransactionKind
}
